import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  MenuItem,
  Typography,
  Divider,
} from "@mui/material";
import axios from "../../../utils/axiosInstance";
import { toast } from "react-toastify";
import { getUIDescription } from "../customerGroupDetails/AssigningCustomerGroupDevice";

const EditTechnicianDialog = ({
  open,
  onClose,
  technician,
  groups = [],
  group,
  currentRegion,
  regions = [],
  onSave,
}) => {
  const [formData, setFormData] = useState({
    regionId: "",
    name: "",
    group: "",
    region: "",
    notes: "",
  });

  const [loading, setLoading] = useState(false);

  // Initialize form with technician's current values
  useEffect(() => {
    if (technician) {
      const currentRegion = technician.region || "";
      const currentGroup = technician.group || "";
      const currentRegionId = technician.regionId || "";

      setFormData({
        regionId: currentRegionId,
        name: technician.name || "",
        group: currentGroup,
        region: currentRegion,
        notes: technician.notes || "",
      });
    }
    getTechnicianData();
  }, [technician]);

  const handleChange = (field) => (event) => {
    const value = event.target.value;

    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // If region is changed, update regionId
    if (field === "region") {
      const selectedRegion = regions.find((r) => r.name === value);
      if (selectedRegion) {
        setFormData((prev) => ({
          ...prev,
          regionId: selectedRegion.id,
        }));
      }
    }
  };

  const getTechnicianData = async () => {
    if (!technician?.id) return;

    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_API_GATEAWAY}/routes/${technician.id}`
      );

      // Update form data with the fetched description/notes
      if (response.data) {
        setFormData((prev) => ({
          ...prev,
          notes: response.data.description || "",
        }));
      }
    } catch (error) {
      console.error("Error fetching technician data:", error);
      toast.error("Failed to load technician details");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateTechnician = async () => {
    if (!technician?.id) {
      console.error("No technician selected for update");
      toast.error("No technician selected");
      return;
    }

    // Validation
    if (!formData.name.trim()) {
      toast.error("Technician name is required");
      return;
    }

    if (!formData.regionId) {
      toast.error("Region is required");
      return;
    }

    console.log("currentRegion:", currentRegion);
    console.log("formData:", formData);
    console.log("technician:", technician);

    // Find the selected region ID based on the region name in formData
    const selectedRegion = regions.find((r) => r.name === formData.region);
    const regionIdToUse = selectedRegion?.id || formData.regionId;

    const payload = {
      regionId: regionIdToUse,
      groupId: group.id,
      name: formData.name,
      description: formData.notes || "",
      UI_Description: `${getUIDescription()}\n Edit Technician (Route)`, 
    };

    console.log("Update payload:", payload);

    setLoading(true);
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_GATEAWAY}/routes/${technician.id}`,
        payload
      );

      toast.success("Technician updated successfully");

      // Call onSave callback if provided
      if (onSave) {
        onSave(response.data);
      }

      // Close dialog
      handleCancel();
    } catch (error) {
      console.error("Error updating technician:", error);
      toast.error(
        error.response?.data?.message || "Failed to update technician"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      regionId: "",
      name: "",
      group: "",
      region: "",
      notes: "",
    });
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
        },
      }}
    >
      <DialogTitle>
        <Typography fontWeight="bold">
          Edit Technician "{technician?.name || "Technician"}"
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3, mt: 1 }}>
          {/* Name Field */}
          <Box>
            <Typography variant="body2" fontWeight="medium" sx={{ mb: 1 }}>
              Name *
            </Typography>
            <TextField
             sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 0, // ✅ remove rounding
                      },
                    }}
              fullWidth
              size="small"
              value={formData.name}
              onChange={handleChange("name")}
              placeholder="Enter technician name"
              required
            />
          </Box>

          <Divider />

          {/* Group Field */}
          <Box>
            <Typography variant="body2" fontWeight="medium" sx={{ mb: 1 }}>
              Group
            </Typography>
            <TextField
             sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 0, // ✅ remove rounding
                      },
                    }}
              select
              fullWidth
              size="small"
              value={formData.group}
              onChange={handleChange("group")}
              disabled
            >
              {groups.map((group) => (
                <MenuItem key={group.id} value={group.name}>
                  {group.name}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <Divider />

          {/* Region Field */}
          <Box>
            <Typography variant="body2" fontWeight="medium" sx={{ mb: 1 }}>
              Region *
            </Typography>
            <TextField
             sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 0, // ✅ remove rounding
                      },
                    }}
              select
              fullWidth
              size="small"
              value={formData.region}
              onChange={handleChange("region")}
              required
            >
              {regions.map((region) => (
                <MenuItem key={region.id} value={region.name}>
                  {region.name}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <Divider />

          {/* Notes Field */}
          <Box>
            <Typography variant="body2" fontWeight="medium" sx={{ mb: 1 }}>
              Notes
            </Typography>
            <TextField
             sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 0, // ✅ remove rounding
                      },
                    }}
              fullWidth
              multiline
              rows={3}
              value={formData.notes}
              onChange={handleChange("notes")}
              placeholder="Notes about this technician..."
            />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
        <Button
          onClick={handleCancel}
          variant="outlined"
          sx={{ minWidth: 100,borderRadius: 0 }}
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          onClick={handleUpdateTechnician}
          variant="contained"
          sx={{ minWidth: 120 ,borderRadius: 0,}}
          disabled={loading}
        >
          {loading ? "Updating..." : "Update Technician"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditTechnicianDialog;
